import { motion } from "framer-motion";
import { Sun, Award, Sparkles, Leaf, Building2, Users } from "lucide-react";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";

const pillars = [
  { icon: Sun, title: "Comfort", desc: "Every day should begin with comfort. It's our starting point for everything we build." },
  { icon: Award, title: "Quality", desc: "Dependable performance that families can trust — again and again." },
  { icon: Sparkles, title: "Value", desc: "Premium products at prices real households can love." },
  { icon: Leaf, title: "Responsibility", desc: "Efficient manufacturing, recyclable packaging where practical, and measurable improvements." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="about-page">
      <SiteHeader />
      <CartDrawer />

      {/* Hero */}
      <section className="border-b border-slate-100 bg-gradient-to-b from-sky-50/60 to-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 md:py-28">
          <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold">Our story</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl font-black tracking-tight text-[#0f2a5c] leading-[1.02]">
            The future of<br /><em className="text-amber-500 not-italic">everyday</em> comfort.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="mt-8 max-w-2xl text-lg text-slate-600 leading-relaxed">
            Morning Soft LLC is a U.S.-based consumer products company building high-quality household paper
            and personal-care products for today&apos;s families — designed around a single, powerful idea.
          </motion.p>
          <p className="mt-6 font-display text-2xl font-bold text-[#173a7a] italic">
            &ldquo;Every day should begin with comfort.&rdquo;
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold text-center">What we stand for</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-black text-[#0f2a5c] text-center">
          Everyday Premium™
        </h2>
        <p className="text-center text-slate-600 mt-3 max-w-xl mx-auto">
          Premium comfort. Practical value. Made for everyday life.
        </p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <motion.div key={p.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-3xl border border-slate-100 p-6 hover:border-[#173a7a] hover:shadow-md transition-all"
              data-testid={`pillar-${p.title.toLowerCase()}`}
            >
              <div className="w-12 h-12 rounded-full bg-sky-50 flex items-center justify-center text-[#173a7a]">
                <p.icon size={22} />
              </div>
              <p className="mt-4 font-display font-bold text-lg text-slate-900">{p.title}</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Product family */}
      <section className="bg-slate-50/60 border-y border-slate-100">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold">More than tissue</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-black text-[#0f2a5c] leading-tight">
              One product. One family. One household at a time.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Morning Soft begins with household paper products, but our vision extends much further —
              bathroom tissue, kitchen towels, facial tissue, baby wipes, adult wipes, and future
              household-care innovations. The Morning Soft name will represent something simple:
              products designed to make everyday life more comfortable.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li>· Softness you can feel.</li>
              <li>· Quality you can trust.</li>
              <li>· Value that makes sense.</li>
              <li>· Innovation with purpose.</li>
              <li>· Comfort for everyday life.</li>
            </ul>
          </div>
          <img src="/products/img_05.png" alt="Morning Soft factory" className="rounded-3xl w-full object-cover shadow-lg" />
        </div>
      </section>

      {/* Footer band */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="flex flex-col items-start gap-3">
          <Building2 size={24} className="text-[#173a7a]" />
          <p className="font-display font-bold text-slate-900">Made for American families</p>
          <p className="text-sm text-slate-600">Angleton, Texas · Morning Soft LLC</p>
        </div>
        <div className="flex flex-col items-start gap-3">
          <Users size={24} className="text-[#173a7a]" />
          <p className="font-display font-bold text-slate-900">Built for the long term</p>
          <p className="text-sm text-slate-600">Homes, retailers, hotels, healthcare, offices, restaurants and schools.</p>
        </div>
        <div className="flex flex-col items-start gap-3">
          <Sun size={24} className="text-amber-500" />
          <p className="font-display font-bold text-slate-900">Start Your Day in Comfort</p>
          <p className="text-sm text-slate-600">One product. One family. One household at a time.</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
